import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DatePickerField } from "@/components/ui/date-picker-field";
import { TimePickerField } from "@/components/ui/time-picker-field";
import { Loader2, Copy, Save, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { useServiceAreas } from "@/hooks/useServiceAreas";
import type { ReferralLead } from "@/hooks/useReferralLeads";
import { canonicalSourceType } from "@/lib/referral-sources";
import { isValidEmail, toLocalYMD, getEdgeFunctionErrorMessage } from "@/lib/validation";

const SOURCE_TYPE_GROUPS = [
  {
    label: "Care Transition & Clinical",
    types: [
      "Hospital Discharge Planner",
      "Case Manager",
      "Home Health Agency",
      "Physician Office / Primary Care",
      "Physical / Occupational Therapist",
      "Clinic (Specialty / Urgent Care)",
      "Rehab Center",
      "Social Worker",
    ],
  },
  {
    label: "Senior Living & Housing",
    types: [
      "Assisted Living Facility",
      "Senior Living Facility",
      "Area Agency on Aging (AAA)",
    ],
  },
  {
    label: "Legal, Financial & Advisory",
    types: ["Senior Legal Guardian", "Financial / Estate Planner"],
  },
  {
    label: "Benefits & Government",
    types: ["Veterans Administration Program"],
  },
  {
    label: "Relationship-Based",
    types: ["Former Client / Family", "Existing Client / Family"],
  },
];

const TOKENS = [
  { label: "{agency_name}", value: "{agency_name}" },
  { label: "{owner_name}", value: "{owner_name}" },
  { label: "{specialty}", value: "{specialty}" },
  { label: "{service_area}", value: "{service_area}" },
  { label: "{contact_name}", value: "{contact_name}" },
  { label: "{org_name}", value: "{org_name}" },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: ReferralLead;
  onUpdate: (id: string, updates: Partial<ReferralLead>) => Promise<void>;
}

export default function EmailComposerModal({ open, onOpenChange, lead, onUpdate }: Props) {
  const { profile } = useProfile();
  const { serviceAreas } = useServiceAreas();
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const [sourceType, setSourceType] = useState(canonicalSourceType(lead.source_type) || "");
  const [subject, setSubject] = useState(lead.email_draft_subject || "");
  const [body, setBody] = useState(lead.email_draft_body || "");
  const [contactEmail, setContactEmail] = useState(lead.contact_email || "");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState<string | null>(null);
  const [schedulePopoverOpen, setSchedulePopoverOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>();
  const [scheduleTime, setScheduleTime] = useState("09:00");

  const emailValid = isValidEmail(contactEmail.trim());
  const canSend = emailValid && !!subject.trim() && !!body.trim();

  useEffect(() => {
    if (open) {
      setSourceType(canonicalSourceType(lead.source_type) || "");
      setSubject(lead.email_draft_subject || "");
      setBody(lead.email_draft_body || "");
      setContactEmail(lead.contact_email || "");
    }
  }, [open, lead]);

  async function generateEmail(type: string) {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-outreach-email", {
        body: {
          source_type: type,
          contact_name: lead.contact_name || "",
          org_name: lead.org_name,
          agency_name: profile?.agency_name || "",
          owner_name: profile?.owner_name || "",
          specialties: profile?.service_specialties || [],
          service_area_zips: serviceAreas.map(sa => sa.zip_code),
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setSubject(data.subject);
      setBody(data.body);
      setSourceType(type);
      // Auto-save draft
      await onUpdate(lead.id, {
        email_draft_subject: data.subject,
        email_draft_body: data.body,
        email_status: "Draft",
        source_type: type,
      } as any);
      toast.success("Email draft generated");
    } catch (e: any) {
      toast.error(e.message || "Failed to generate email");
    } finally {
      setGenerating(false);
    }
  }

  function handleSourceTypeChange(newType: string) {
    if ((subject || body) && newType !== sourceType) {
      setConfirmRegenerate(newType);
    } else {
      generateEmail(newType);
    }
  }

  async function handleSaveDraft() {
    setSaving(true);
    try {
      await onUpdate(lead.id, {
        email_draft_subject: subject,
        email_draft_body: body,
        email_status: "Draft",
        contact_email: contactEmail.trim() || null,
      } as any);
      toast.success("Draft saved");
    } catch {
      toast.error("Failed to save draft");
    } finally {
      setSaving(false);
    }
  }

  function handleCopy() {
    const text = `Subject: ${subject}\n\n${body}`;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  }

  async function handleSendNow() {
    if (!canSend) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-outreach-email", {
        body: { referral_lead_id: lead.id, to: contactEmail.trim(), subject, body },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      // Edge function already wrote the new status server-side; refresh the
      // parent's list/local state to match instead of leaving it stale.
      await onUpdate(lead.id, {
        email_status: "Sent",
        last_contacted_at: new Date().toISOString(),
        contact_email: contactEmail.trim(),
        email_draft_subject: subject,
        email_draft_body: body,
      } as any);
      toast.success(`Email sent to ${contactEmail.trim()}`);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(await getEdgeFunctionErrorMessage(e, "Failed to send email"));
    } finally {
      setSending(false);
    }
  }

  async function handleConfirmSchedule() {
    if (!canSend || !scheduleDate) return;
    const scheduleAt = new Date(`${toLocalYMD(scheduleDate)}T${scheduleTime}:00`);
    if (scheduleAt.getTime() <= Date.now()) {
      toast.error("Pick a future date and time");
      return;
    }
    setScheduling(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-outreach-email", {
        body: { referral_lead_id: lead.id, to: contactEmail.trim(), subject, body, schedule_at: scheduleAt.toISOString() },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await onUpdate(lead.id, {
        email_status: "Scheduled",
        send_scheduled_at: scheduleAt.toISOString(),
        contact_email: contactEmail.trim(),
        email_draft_subject: subject,
        email_draft_body: body,
      } as any);
      toast.success(`Email scheduled for ${scheduleAt.toLocaleString()}`);
      setSchedulePopoverOpen(false);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(await getEdgeFunctionErrorMessage(e, "Failed to schedule email"));
    } finally {
      setScheduling(false);
    }
  }

  function insertToken(token: string) {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newBody = body.substring(0, start) + token + body.substring(end);
    setBody(newBody);
    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + token.length;
    }, 0);
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Composer</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Source Type */}
            <div className="space-y-1">
              <Label>Select Referral Source Type</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={sourceType}
                onChange={e => handleSourceTypeChange(e.target.value)}
                disabled={generating}
              >
                <option value="">— Select source type —</option>
                {SOURCE_TYPE_GROUPS.map(group => (
                  <optgroup key={group.label} label={group.label}>
                    {group.types.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {generating && (
              <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Generating personalized email…</span>
              </div>
            )}

            {!generating && (
              <>
                {/* To */}
                <div className="space-y-1">
                  <Label>To — {lead.contact_name || "Contact"} at {lead.org_name}</Label>
                  <Input
                    type="email"
                    value={contactEmail}
                    onChange={e => setContactEmail(e.target.value)}
                    placeholder="contact@organization.com"
                    maxLength={255}
                  />
                  {contactEmail.trim() && !emailValid && (
                    <p className="text-xs text-destructive">Enter a valid email address</p>
                  )}
                  {!contactEmail.trim() && (
                    <p className="text-xs text-muted-foreground">No email on file for this partner yet — enter one to send.</p>
                  )}
                </div>

                {/* Subject */}
                <div className="space-y-1">
                  <Label>Subject</Label>
                  <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject line" />
                </div>

                {/* Body + Tokens */}
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
                  <div className="space-y-1">
                    <Label>Body</Label>
                    <Textarea
                      ref={bodyRef}
                      value={body}
                      onChange={e => setBody(e.target.value)}
                      rows={12}
                      placeholder="Email body will appear here after selecting a source type…"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Insert Token</Label>
                    <div className="flex flex-col gap-1">
                      {TOKENS.map(t => (
                        <Badge
                          key={t.value}
                          variant="outline"
                          className="cursor-pointer hover:bg-accent text-xs justify-center"
                          onClick={() => insertToken(t.value)}
                        >
                          {t.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button onClick={handleSaveDraft} disabled={saving || !subject} variant="outline" className="gap-2">
                    <Save className="h-4 w-4" />
                    {saving ? "Saving…" : "Save Draft"}
                  </Button>
                  <Button onClick={handleCopy} disabled={!subject} variant="outline" className="gap-2">
                    <Copy className="h-4 w-4" />
                    Copy to Clipboard
                  </Button>
                  <Button
                    onClick={handleSendNow}
                    disabled={!canSend || sending || scheduling}
                    variant="outline"
                    className="gap-2"
                    title={canSend ? undefined : "Enter a valid recipient email, subject, and body first"}
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {sending ? "Sending…" : "Send Now"}
                  </Button>
                  <Popover open={schedulePopoverOpen} onOpenChange={setSchedulePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        disabled={!canSend || sending || scheduling}
                        variant="outline"
                        className="gap-2"
                        title={canSend ? undefined : "Enter a valid recipient email, subject, and body first"}
                      >
                        <Clock className="h-4 w-4" />
                        Schedule
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 space-y-3" align="start">
                      <div className="space-y-1">
                        <Label className="text-xs">Date</Label>
                        <DatePickerField value={scheduleDate} onChange={setScheduleDate} placeholder="Select date" disabled={d => d < new Date(new Date().toDateString())} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Time</Label>
                        <TimePickerField value={scheduleTime} onChange={setScheduleTime} />
                      </div>
                      <Button className="w-full gap-2" onClick={handleConfirmSchedule} disabled={!scheduleDate || scheduling}>
                        {scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
                        {scheduling ? "Scheduling…" : "Confirm Schedule"}
                      </Button>
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmRegenerate} onOpenChange={() => setConfirmRegenerate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace current draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace your current draft with a new email generated for the selected source type.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (confirmRegenerate) generateEmail(confirmRegenerate);
              setConfirmRegenerate(null);
            }}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
